import React from "react";
import CustomTable from "../components/CustomTable";
import SearchAndFilterBar from "../components/SearchAndFilterBar";
import Loading from "../components/Loading";
import MobilePatternHeader from "../components/MobilePatternHeader";
import { StatusBadge } from "../../../constants/statusConstants";
import { ReactComponent as BlankIcon } from "../../../assets/icons/blank.svg";
import { ReactComponent as CopyIcon } from "../../../assets/icons/copy.svg";
import { ReactComponent as CopiedIcon } from "../../../assets/icons/copied.svg";
import Button from "../components/Button";

const PaymentHistoryPresenter = ({
  orders,
  pagination,
  loading,
  searchTerm,
  currentPage,
  setCurrentPage,
  handleSearchChange,
  handleSearchClick,
  handleViewDetail,
  showDetail,
  setShowDetail,
  selectedOrder,
  copiedInvoice,
  handleCopyInvoice,
  formatCurrency,
  formatDate,
  navigate,
}) => {
  const columns = [
    {
      key: "orderCode",
      title: "Mã đơn hàng",
      align: "text-left",
      minWidth: "140px",
      render: (value) => (
        <span className="font-bold text-gray-900 group-hover:text-[#0457C2] transition-colors">
          {value}
        </span>
      ),
    },
    {
      key: "customer",
      title: "Thông tin khách hàng",
      align: "text-left",
      minWidth: "220px",
      render: (value) => (
        <div className="flex flex-col">
          <span className="text-gray-900 font-medium">
            {value?.name || "N/A"}
          </span>
          <span className="text-gray-400 text-xs truncate max-w-[200px]">
            {value?.email || ""}
          </span>
        </div>
      ),
    },
    {
      key: "profile",
      title: "Hồ sơ",
      align: "text-left",
      minWidth: "180px",
      render: (value) => (
        <span className="text-gray-600 italic text-sm">
          {value?.profile || ""}
        </span>
      ),
    },
    {
      key: "totalAmount",
      title: "Tổng tiền",
      align: "text-right",
      minWidth: "130px",
      render: (value) => (
        <span className="font-black text-gray-900">
          {formatCurrency(value)}
        </span>
      ),
    },
    {
      key: "status",
      title: "Trạng thái",
      align: "text-center",
      minWidth: "150px",
      render: (value) => <StatusBadge status={value} />,
    },
    {
      key: "createdAt",
      title: "Ngày tạo",
      align: "text-right",
      minWidth: "180px",
      render: (value) => (
        <span className="text-gray-400 font-medium text-sm">
          {formatDate(value)}
        </span>
      ),
    },
  ];

  return (
    <div className="h-full min-h-0 flex flex-col bg-gray-50/50">
      <MobilePatternHeader
        title="Lịch sử thanh toán"
        onBack={() => navigate(-1)}
        className="md:hidden"
      />

      <div className="p-4 sm:p-6 lg:p-10 flex-1 min-h-0 flex flex-col gap-8 overflow-hidden">
        <div className="shrink-0 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
          <div className="hidden md:block">
            <h1 className="text-3xl font-black text-gray-900 tracking-tight">
              Lịch sử thanh toán
            </h1>
            <p className="text-gray-500 mt-1">
              Quản lý và theo dõi các giao dịch của bạn
            </p>
          </div>

          <div className="w-full lg:w-[400px] animate-in slide-in-from-right-4 duration-500">
            <SearchAndFilterBar
              searchPlaceholder="Tìm mã đơn hàng..."
              searchValue={searchTerm}
              onSearchChange={handleSearchChange}
              onSearchClick={handleSearchClick}
              showDatePickers={false}
              showFilter={false}
              showAdditionBtn={false}
              filterButton={false}
              showAddButton={false}
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 bg-white border border-gray-100 rounded-[32px] overflow-hidden shadow-sm flex flex-col">
          {orders.length === 0 && !loading ? (
            <div className="flex-1 flex flex-col items-center justify-center p-12 text-center animate-in fade-in duration-700">
              <div className="bg-gray-50 p-6 rounded-full mb-4">
                <svg
                  className="h-16 w-16 text-gray-300"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Không tìm thấy đơn hàng
              </h3>
              <p className="text-gray-500 mt-2 max-w-xs">
                Chúng tôi không tìm thấy bất kỳ giao dịch nào phù hợp với tìm
                kiếm của bạn.
              </p>
            </div>
          ) : (
            <div className="flex-1 min-h-0 table-container">
              <CustomTable
                columns={columns}
                data={orders}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={pagination?.totalPages || 1}
                setView={handleViewDetail}
                setEdit={false}
                setDelete={false}
                setDuplicate={false}
                rowIdKey="orderId"
              />
            </div>
          )}
        </div>
      </div>

      {loading && <Loading />}

      {showDetail && selectedOrder && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[100] animate-in fade-in duration-300">
          <div className="bg-white rounded-[32px] shadow-2xl w-full max-w-[650px] max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-300">
            <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0 z-10">
              <div>
                <h2 className="text-2xl font-black text-gray-900 tracking-tight">
                  Chi tiết đơn hàng
                </h2>
                <p className="text-sm text-gray-500 font-medium uppercase tracking-wider mt-0.5">
                  Mã: {selectedOrder.orderCode}
                </p>
              </div>
              <button
                onClick={() => setShowDetail(false)}
                className="text-gray-400 hover:text-gray-900 hover:bg-gray-100 p-2 rounded-xl transition-all active:scale-90"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50/50 p-6 rounded-[24px] border border-gray-100">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                    Thành tiền
                  </label>
                  <p className="text-2xl font-black text-[#0457C2]">
                    {formatCurrency(selectedOrder.totalAmount)}
                  </p>
                </div>
                <div className="space-y-1 flex flex-col md:items-end">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                    Trạng thái
                  </label>
                  <div className="mt-1">
                    <StatusBadge status={selectedOrder.status} />
                  </div>
                </div>
                <div className="space-y-1 pt-2 border-t border-gray-100 md:col-span-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                    Ngày thanh toán
                  </label>
                  <p className="text-gray-700 font-semibold">
                    {formatDate(selectedOrder.createdAt)}
                  </p>
                </div>
              </div>

              {selectedOrder.customer && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">
                      Thông tin khách hàng
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pl-11">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-gray-400">
                        Họ và tên
                      </label>
                      <p className="font-bold text-gray-800">
                        {selectedOrder.customer.name || "N/A"}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-gray-400">
                        Email liên hệ
                      </label>
                      <p className="font-bold text-gray-800 break-all">
                        {selectedOrder.customer.email || "N/A"}
                      </p>
                    </div>
                    {selectedOrder.profile?.brand && (
                      <div className="sm:col-span-2 space-y-1">
                        <label className="text-xs font-semibold text-gray-400">
                          Nhãn hiệu liên quan
                        </label>
                        <p className="font-bold text-gray-800">
                          {selectedOrder.profile.brand}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(selectedOrder.invoice || selectedOrder.note) && (
                <div className="space-y-4 pt-4 border-t border-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">
                      Thông tin bổ sung
                    </h3>
                  </div>

                  <div className="space-y-6 pl-11">
                    {selectedOrder.invoice && (
                      <div className="space-y-3">
                        <label className="text-xs font-semibold text-gray-400">
                          Hóa đơn điện tử
                        </label>
                        <div className="bg-white border border-gray-200 rounded-[20px] p-4 flex items-center justify-between hover:border-blue-300 transition-colors shadow-sm">
                          <a
                            href={selectedOrder.invoice}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 text-[#0457C2] font-bold hover:underline"
                          >
                            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                              <BlankIcon className="w-6 h-6" />
                            </div>
                            Xem hóa đơn chi tiết
                          </a>
                          <button
                            onClick={handleCopyInvoice}
                            className={`p-2.5 rounded-xl transition-all active:scale-90 ${copiedInvoice ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
                            title={
                              copiedInvoice ? "Đã sao chép" : "Sao chép link"
                            }
                          >
                            {copiedInvoice ? (
                              <CopiedIcon className="w-6 h-6" />
                            ) : (
                              <CopyIcon className="w-6 h-6" />
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {selectedOrder.note && (
                      <div className="space-y-2">
                        <label className="text-xs font-semibold text-gray-400">
                          Ghi chú từ quản trị viên
                        </label>
                        <div className="bg-gray-50 p-4 rounded-[16px] text-sm text-gray-700 leading-relaxed italic border-l-4 border-blue-400">
                          {selectedOrder.note}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="px-8 py-6 border-t border-gray-100 flex justify-end bg-white">
              <Button variant="secondary" onClick={() => setShowDetail(false)}>
                Đóng chi tiết
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentHistoryPresenter;
